import {eye} from 'assets/images/images';
import {
  DashboardLayout,
  EmptyState,
  PrimaryButton,
  TableLayout,
  Loader,
} from 'components';
import Breadcrumbs from 'components/BreadCrumbs';
import {PAGES_URL} from 'helpers/constants';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import NameFlowTemplateModal from 'components/NameFlowTemplateModal';
import {useQuery} from 'react-query';
import {
  fetchTemplateRuleDetails,
  fetchTemplateRules,
} from '../../../../requests/queries/flows';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {getStatusColor} from '../../../../helpers';
import {RuleDetails} from './RuleDetails';

function FlowTemplateDetails() {
  const navigate = useNavigate();
  const [nameFlow, setNameFlow] = useState(false);
  const [selectedRule, setSelectedRule] = useState({});
  const {slug} = useParams();
  const {state} = useLocation();

  const {data, isLoading} = useQuery(['flow-templates-rule', slug], () =>
    fetchTemplateRules(slug),
  );
  const {data: ruleDetails, isLoading: detailsLoading} = useQuery(
    ['flow-templates-rule-details', selectedRule?.selectedId],
    () =>
      fetchTemplateRuleDetails({
        template_id: slug,
        rule_id: selectedRule?.selectedId,
      }),
    {
      enabled: !!selectedRule?.selectedId,
    },
  );

  const ruleDetailsData = useMemo(() => {
    if (!detailsLoading && ruleDetails) {
      return {...ruleDetails?.entity, description: selectedRule?.description};
    }
    return {};
  }, [detailsLoading, ruleDetails, selectedRule?.description]);

  const TEMPLATE_RULES = useMemo(
    () => (!isLoading && data ? data?.entity?.rules : []),
    [data, isLoading],
  );

  const handleSelectRule = useCallback(
    rule => {
      setSelectedRule({
        selectedId: rule?.id,
        ruleName: rule?.name || 'N/A',
        description: rule?.description || 'N/A',
      });

      const searchParams = new URLSearchParams(document.location.search);
      searchParams.set('rule_id', rule?.id);
      const newSearchParams = searchParams.toString();
      navigate(`?${newSearchParams}`, {
        state,
      });
    },
    [navigate, state],
  );

  useEffect(() => {
    if (TEMPLATE_RULES.length > 0 && !selectedRule?.selectedId) {
      const firstRule = TEMPLATE_RULES[0];
      handleSelectRule(firstRule);
    }
  }, [TEMPLATE_RULES, handleSelectRule, selectedRule?.selectedId]);

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Flow Templates</p>
      }
    >
      <div className="flex items-center min-h-[52px] py-2 flex-wrap gap-3 justify-between mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E3E6]">
        <Breadcrumbs
          items={[
            {label: 'Flow Templates', path: PAGES_URL.FLOW_TEMPLATES},
            {label: state?.template?.name || 'N/A'},
          ]}
        />

        {TEMPLATE_RULES?.length !== 0 && (
          <PrimaryButton
            buttonText="Use This Template"
            onClick={() => setNameFlow(true)}
          />
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : !isLoading && TEMPLATE_RULES?.length === 0 ? (
        <EmptyState
          noBtn
          heading="No Data found"
          body="No rule found for this template"
          height={70}
        />
      ) : (
        <div className="grid lg:grid-cols-[56%_44%] gap-7 mt-1">
          <TableLayout negativeMargins>
            <thead className="font-semibold uppercase">
              <tr className="bg-white80">
                <th className="pl-4 sm:pl-6 xl:pl-8 h-11 w-[35%] font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>rules</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left w-[22%] h-11 text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>risk level</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 w-[26%] text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>score</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 w-[10%] text-small text-[#5C688E] whitespace-nowrap">
                  <span>action</span>
                </th>
              </tr>
            </thead>

            <tbody className="text-sm bg-white">
              {!isLoading &&
                TEMPLATE_RULES?.map(rule => (
                  <tr
                    key={rule?.id}
                    className={`border-[0.5px] border-[#E4E3EC] cursor-pointer hover:bg-[#F2F6FD] ${
                      selectedRule?.selectedId === rule?.id
                        ? 'bg-[#F2F6FD] '
                        : ''
                    }`}
                    onClick={() => handleSelectRule(rule)}
                  >
                    <td className="p-4 pl-4 sm:pl-6 xl:pl-8 w-[35%] text-body whitespace-nowrap capitalize">
                      {rule?.name || 'N/A'}
                    </td>
                    <td className="p-4 pl-0 text-body w-[22%] whitespace-nowrap">
                      <span
                        className={`flex uppercase items-center justify-center py-1 px-2 w-fit  ${getStatusColor(
                          rule?.event?.type,
                        )} rounded-full text-small`}
                      >
                        {rule?.event?.type?.replace('_', ' ') || 'N/A'}
                      </span>
                    </td>
                    <td className="capitalize p-4 pl-0 w-[26%] text-tiny text-body whitespace-nowrap">
                      {rule?.event?.params?.risk_score || 'N/A'}
                    </td>
                    <td className="p-4 pl-0 text-body w-[10%] whitespace-nowrap">
                      <img src={eye} alt="icon" />
                    </td>
                  </tr>
                ))}
            </tbody>
          </TableLayout>

          {!selectedRule?.selectedId ? (
            <div className="min-h-52 flex flex-col justify-center items-center ml-7 mt-5 rounded-xl  lg:mr-8 bg-[#F2F6FD] border border-[#D9E5F8]">
              <h4 className="text-deepBlue text-lg font-semibold mb-2">
                No Rule Selected
              </h4>
              <p className="text-deepBlue text-tiny  mb-2">
                Please select a rule from the list on the left to view its
                details.
              </p>
            </div>
          ) : detailsLoading ? (
            <div className="ml-7 mt-5 rounded-xl  lg:mr-8 bg-[#F2F6FD] border border-[#D9E5F8]">
              <Loader height={45} />
            </div>
          ) : (
            <RuleDetails
              ruleDetails={ruleDetailsData}
              ruleName={selectedRule?.ruleName}
            />
          )}

          <NameFlowTemplateModal
            open={nameFlow}
            setOpen={setNameFlow}
            template={state?.template}
          />
        </div>
      )}
    </DashboardLayout>
  );
}

export default FlowTemplateDetails;
